import React, { createContext, PropsWithChildren, useContext } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { useAllAutoDocumentExtractions } from 'hooks/study/useAllAutoDocumentExtractions';
import { DocumentViewerContext } from 'contexts/DocumentViewerProvider';
import {
  AutoDocumentExtraction,
  AutoDocumentExtractionsByField,
} from 'query/study';

type AutoDocExtSuggestionContextInputs = {
  // Used by DE1 SuggestedField for tracking-event payloads. Not read by
  // useAutoDocExtSuggestion itself — kept here so a single provider serves
  // both DE1 and DE2 consumers without each one fishing it out separately.
  reviewId: string;
  studyId: number;
  useSuggestions: boolean;
};

type AutoDocExtSuggestionContextOutputs = AutoDocExtSuggestionContextInputs & {
  validDocument: boolean;
  documentId: number | undefined;
  allSuggestions: UseQueryResult;
};

const AutoDocExtSuggestionContext = createContext<AutoDocExtSuggestionContextOutputs | null>(
  null
);

export const getSingleExtraction = (
  query: UseQueryResult,
  autoDocExtField: string | undefined
): AutoDocumentExtraction | null => {
  if (autoDocExtField && query.data) {
    return (
      (query.data as AutoDocumentExtractionsByField)[autoDocExtField] || null
    );
  } else {
    return null;
  }
};

export const useAutoDocExtSuggestionContext = (): AutoDocExtSuggestionContextOutputs => {
  const context = useContext(AutoDocExtSuggestionContext);

  if (context === null) {
    throw new Error(
      'useAutoDocExtSuggestion called outside of an AutoDocExtSuggestionProvider'
    );
  }

  return context;
};

export const AutoDocExtSuggestionProvider = ({
  reviewId,
  studyId,
  useSuggestions,
  children,
}: PropsWithChildren<AutoDocExtSuggestionContextInputs>) => {
  const selectedDocument = useContext(DocumentViewerContext)?.selectedDocument;
  const validDocument = selectedDocument?.extractable ?? false;
  const documentId = selectedDocument?.id ?? undefined;

  const allSuggestions = useAllAutoDocumentExtractions({
    enabled: useSuggestions && validDocument,
    studyId,
    documentId,
  });

  return (
    <AutoDocExtSuggestionContext.Provider
      value={{
        reviewId,
        studyId,
        useSuggestions,
        validDocument,
        documentId,
        allSuggestions,
      }}
    >
      {children}
    </AutoDocExtSuggestionContext.Provider>
  );
};
